import { Btn, Eyebrow, PageHero } from "@/components/site/bits";
import { Reveal } from "@/components/site/Reveal";
import { NcsDiagram } from "@/components/site/NcsDiagram";
import { BOOK_URL, IMAGES } from "@/data/site";

const pillars = [
    {
        id: "neuro",
        title: "Neuro",
        subtitle: "Understanding the addictive loop",
        text: "How addiction rewires the brain — craving, reward, and the loop that keeps the cycle going. When you understand the loop, you can begin to interrupt it.",
    },
    {
        id: "clinical",
        title: "Clinical",
        subtitle: "Behavioral patterns & impulse control",
        text: "Recognizing triggers, warning signs, and learned behaviors — and replacing them with practical tools for long-term change.",
    },
    {
        id: "spiritual",
        title: "Spiritual",
        subtitle: "Divine intervention",
        text: "A Christ-centered foundation of faith, hope, and surrender that restores purpose and dignity to the recovery journey.",
    },
    {
        id: "healing",
        title: "Healing",
        subtitle: "Total reconstruction of self",
        text: "Recovery is more than stopping a behavior — it is rebuilding identity, relationships, and a life worth staying sober for.",
    },
];

export default function Framework() {
    return (
        <main>
            <PageHero
                testid="framework-hero"
                eyebrow="The NCS Framework"
                title="A More Complete Approach to Understanding Recovery"
                sub="Rather than focusing only on the addiction itself, the NCS Framework looks at the whole person — thought patterns, behaviors, triggers, the subconscious mind, and other Neuro-Bio-Spiritual Dynamics that affect long-term change."
                image={IMAGES.framework}
            />
            <section className="bg-white">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 sm:py-24 lg:grid-cols-2">
                    <Reveal>
                        <Eyebrow>Four Dimensions, One Person</Eyebrow>
                        <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B1F3A]">
                            Understand the Cycle. Change the Outcome.
                        </h2>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-[#4A5568]">
                            The NCS Framework is Dr. Sergio Jaquez’s approach to exploring the different factors that
                            can influence addiction, behavior, relapse, and recovery. Each dimension informs the
                            others — and lasting change comes from addressing all of them together.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Btn testid="framework-page-book-btn" href={BOOK_URL}>Purchase the Book</Btn>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="rounded-2xl bg-[#08162A] p-8 sm:p-10">
                            <NcsDiagram />
                        </div>
                    </Reveal>
                </div>
            </section>
            <section className="bg-[#F9F8F3]">
                <div className="mx-auto max-w-7xl px-6 py-20">
                    <div className="grid gap-8 md:grid-cols-2">
                        {pillars.map((p, i) => (
                            <Reveal key={p.id} delay={i * 0.06}>
                                <div data-testid={`framework-pillar-${p.id}`} className="h-full rounded-xl border-l-4 border-[#C9A227] bg-white p-8 shadow-[0_20px_45px_-30px_rgba(8,22,42,0.4)]">
                                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C9A227]">{p.subtitle}</p>
                                    <h3 className="mt-2 text-xl font-extrabold text-[#0B1F3A]">{p.title}</h3>
                                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#4A5568]">{p.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
